import { useEffect } from "react";
import { toast } from "sonner";
import { BACKEND } from "./api";
import { useMarket, requestMeta } from "./store";
import { useGtt } from "./gtt";

const refs = new Map();
let ws = null;
let sendTimer = null;
let retry = 1000;
let closedByUser = false;
let pingTimer = null;
let tickCount = 0;
let rateTimer = null;

function startPings() {
  clearInterval(pingTimer);
  pingTimer = setInterval(() => { if (ws && ws.readyState === 1) ws.send(JSON.stringify({ action: "ping", t: performance.now() })); }, 2000);
  if (!rateTimer) rateTimer = setInterval(() => { useMarket.getState().setTickRate(tickCount); tickCount = 0; }, 1000);
}

function sendSubs() {
  clearTimeout(sendTimer);
  sendTimer = setTimeout(() => {
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ action: "sub", keys: [...refs.keys()] }));
  }, 120);
}

function beep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = ok ? 880 : 300;
    g.gain.setValueAtTime(0.06, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.25);
  } catch (e) { /* audio not available */ }
}

function onOrder(msg) {
  const o = msg.data;
  const label = `${o.side} ${o.quantity} ${o.symbol}`;
  if (msg.event === "fill") {
    toast.success(o.status === "COMPLETE" ? `Simulated order executed · ${label}` : `Simulated order partially filled · ${o.filled_qty}/${o.quantity} ${o.symbol}`,
      { description: `Avg ₹${o.avg_price}` });
    beep(true);
  } else if (msg.event === "rejected") {
    toast.error(`Simulated order rejected · ${label}`, { description: o.reason });
    beep(false);
  } else if (msg.event === "triggered") {
    toast.info(`Simulated order triggered · ${label}`);
  } else if (msg.event === "cancelled" && o.validity === "IOC") {
    toast.message(`Simulated order · IOC remainder cancelled · ${o.symbol}`);
  }
}

export function connectSocket() {
  const token = localStorage.getItem("pt_token");
  if (!token) return;
  if (ws && ws.readyState <= 1) return;
  closedByUser = false;
  const url = `${BACKEND.replace(/^http/, "ws")}/api/ws?token=${encodeURIComponent(token)}`;
  ws = new WebSocket(url);
  const st = useMarket.getState();
  st.setWsState("connecting");
  ws.onopen = () => { retry = 1000; st.setWsState("open"); sendSubs(); startPings(); };
  ws.onmessage = (ev) => {
    const msg = JSON.parse(ev.data);
    const s = useMarket.getState();
    if (msg.type === "ticks") { tickCount += msg.data.length; s.mergeTicks(msg.data); }
    else if (msg.type === "pong") { if (typeof msg.t === "number") s.addLatency(Math.round(performance.now() - msg.t)); }
    else if (msg.type === "status") s.setStatus(msg.data);
    else if (msg.type === "order") { onOrder(msg); s.bumpPortfolio(); if (msg.event === "fill") window.dispatchEvent(new CustomEvent("pd:fill", { detail: msg.data })); }
    else if (msg.type === "portfolio") s.bumpPortfolio();
    else if (msg.type === "alert") {
      toast.info(`Price alert · ${msg.data.symbol}`, { description: `LTP ₹${msg.data.triggered_ltp}${msg.data.note ? ` · ${msg.data.note}` : ""}`, duration: 10000 });
      beep(true); useGtt.getState().bump();
    } else if (msg.type === "gtt") {
      const g = msg.data;
      (g.order_status === "REJECTED" ? toast.error : toast.success)(`Simulated order · GTT triggered · ${g.side} ${g.quantity} ${g.symbol}`, { description: `Simulated limit order ${g.order_status || "placed"}` });
      beep(g.order_status !== "REJECTED"); useGtt.getState().bump();
    }
  };
  ws.onclose = () => {
    clearInterval(pingTimer);
    useMarket.getState().setWsState("closed");
    if (!closedByUser) setTimeout(connectSocket, retry);
    retry = Math.min(retry * 2, 15000);
  };
}

export function disconnectSocket() {
  closedByUser = true;
  if (ws) ws.close();
  ws = null;
}

export function useSubscribe(keys) {
  const sig = (keys || []).filter(Boolean).join(",");
  useEffect(() => {
    const list = sig ? sig.split(",") : [];
    list.forEach((k) => refs.set(k, (refs.get(k) || 0) + 1));
    requestMeta(list);
    sendSubs();
    return () => {
      list.forEach((k) => {
        const n = (refs.get(k) || 1) - 1;
        if (n <= 0) refs.delete(k); else refs.set(k, n);
      });
      sendSubs();
    };
  }, [sig]);
}

export const useTick = (key) => useMarket((s) => s.ticks[key]);
export const useMeta = (key) => useMarket((s) => s.meta[key]);
